import React, { memo, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CustomerBusinessDetailsForm } from 'src/components/customer/customer-business-details-form/CustomerBusinessDetailsForm'
import { CustomerInsuranceForm } from 'src/components/customer/customer-insurance-form/CustomerInsuranceForm'
import { CustomerSignContract } from 'src/components/customer/customer-sign-contract/CustomerSignContract'
import { CustomerOnboardingWelcome } from 'src/components/customer/CustomerOnboardingWelcome'
import { CustomerStitchPayment } from 'src/components/customer/CustomerStitchPayment'
import { FetchCustomerProfile } from 'src/constants/actionTypes'
import { CustomerUploadBankInformation } from 'src/constants/actionTypes'
import {
  getIsCustomerOnboardingCustomerInformationCompleted,
  getIsCustomerOnboardingPaymentInformationCompleted,
  getIsCustomerOnboardingStarted,
  getNotCompletedCustomerOnboardingCollectDataTypes,
  getCustomerProfile,
  getCustomerOnboardingStepsCount,
  getIsCustomerOnboardingPaymentMethodCompleted,
} from 'src/selectors/customer'
import { CustomerCollectedDataType } from 'src/constants/customer/enums'
import { FullScreenLoader } from 'src/components/shared/FullScreenLoader'
import { CustomerOnboardingCompleted } from 'src/components/customer/CustomerOnboardingCompleted'
import { CustomerTSAForm } from 'src/components/customer/customer-tsa-form/CustomerTSAForm'
import { CustomerBankPaymentForm } from 'src/components/customer/customer-payment-method-form/BankPaymentForm'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { BankInformationRequest } from 'src/components/customer/customer-payment-method-form/BankPaymentForm.interface'
import { CustomerBusinessInformationForm } from 'src/components/customer/customer-business-information-form/CustomerBusinessInformationForm'

const CustomerOnboardingPageComponent = () => {
  const dispatch = useDispatch()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const customerProfile = useSelector(getCustomerProfile)
  const customerOnboardingStepsCount = useSelector(getCustomerOnboardingStepsCount)
  const isCustomerOnboardingStarted = useSelector(getIsCustomerOnboardingStarted)
  const isCustomerOnboardingPartnerInformationCompleted = useSelector(
    getIsCustomerOnboardingCustomerInformationCompleted,
  )
  const isCustomerOnboardingPaymentInformationCompleted = useSelector(
    getIsCustomerOnboardingPaymentInformationCompleted,
  )
  const isCustomerOnboardingPaymentMethodCompleted = useSelector(
    getIsCustomerOnboardingPaymentMethodCompleted,
  )
  const notCompletedCustomerOnboardingCollectDataTypes = useSelector(
    getNotCompletedCustomerOnboardingCollectDataTypes,
  )

  const [isStartPressed, setIsStartPressed] = useState(false)

  const handleStart = useCallback(() => {
    setIsStartPressed(true)
  }, [])

  useEffect(() => {
    const fetchState = async () => {
      dispatch(FetchCustomerProfile.request())
    }

    fetchState()
  }, [])

  const handleSubmitBankInformation = useCallback(
    async (requestBody: BankInformationRequest) => {
      await asyncDispatchWithErrorToast(CustomerUploadBankInformation.request(requestBody))
    },
    [asyncDispatchWithErrorToast],
  )

  if (!customerProfile) {
    return <FullScreenLoader />
  }

  if (!isCustomerOnboardingStarted && !isStartPressed) {
    return <CustomerOnboardingWelcome onNext={handleStart} />
  }

  if (!isCustomerOnboardingPartnerInformationCompleted) {
    return (
      <CustomerBusinessInformationForm stepNumber={1} stepCount={customerOnboardingStepsCount} />
    )
  }

  if (notCompletedCustomerOnboardingCollectDataTypes.includes(CustomerCollectedDataType.TSA)) {
    return <CustomerTSAForm stepNumber={2} stepCount={customerOnboardingStepsCount} />
  }

  if (
    notCompletedCustomerOnboardingCollectDataTypes.includes(CustomerCollectedDataType.Insurance)
  ) {
    return <CustomerInsuranceForm stepNumber={3} stepCount={customerOnboardingStepsCount} />
  }

  if (!isCustomerOnboardingPaymentInformationCompleted) {
    return <CustomerStitchPayment stepNumber={4} stepCount={customerOnboardingStepsCount} />
  }

  if (!isCustomerOnboardingPaymentMethodCompleted) {
    return (
      <CustomerBankPaymentForm
        stepNumber={5}
        stepCount={customerOnboardingStepsCount}
        showCurrentPaymentMethod={false}
        onSubmitBankInformation={handleSubmitBankInformation}
      />
    )
  }

  if (
    notCompletedCustomerOnboardingCollectDataTypes.includes(
      CustomerCollectedDataType.BusinessDetails,
    )
  ) {
    return <CustomerBusinessDetailsForm stepNumber={6} stepCount={customerOnboardingStepsCount} />
  }

  if (
    notCompletedCustomerOnboardingCollectDataTypes.includes(
      CustomerCollectedDataType.GigsafeContract,
    )
  ) {
    return (
      <CustomerSignContract
        stepNumber={customerOnboardingStepsCount}
        stepCount={customerOnboardingStepsCount}
      />
    )
  }

  return <CustomerOnboardingCompleted />
}

export const CustomerOnboardingPage = memo(CustomerOnboardingPageComponent)
